import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import {
  fetchSeoBenchmark,
  fetchSeoDomainOverview,
  fetchSeoProjectOptions,
  type DomainOverviewResult,
} from '@/data/sources';
import type { BenchmarkComparison, BenchmarkResult } from '@/types';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/domain-overview — Domain Overview (W1a). Read-only, source-attributed
 * domain-level intelligence from DataForSEO Labs, plus a directional
 * data-quality benchmark (GSC vs provider, roadmap §9.2).
 *
 * HONESTY + SAFETY INVARIANTS (W1a contract):
 *  - PAID (DataForSEO Labs): NEVER auto-fires. Loading project options is a
 *    free list read; the overview + benchmark run ONLY on an explicit click, so
 *    mounting this page charges nothing. No useQuery is wired to a paid read.
 *  - Every card is source-attributed (source + provider fetched_at + cache
 *    indicator). Provider nulls render as em-dashes — numbers are NEVER invented.
 *  - Honest states throughout: idle / loading / error / not-configured (503).
 *  - The benchmark is DIRECTIONAL — divergence is surfaced, never hard-failed.
 */

/** Map a machine source tag to a human, source-attributed label. */
function sourceLabel(source: string): string {
  if (source.startsWith('dataforseo.labs')) return 'DataForSEO Labs';
  if (source.startsWith('gsc+dataforseo')) return 'Google Search Console + DataForSEO';
  if (source === 'bc_seo_rankings') return 'Stored rank tracking';
  if (source.startsWith('gsc')) return 'Google Search Console';
  if (source.startsWith('dataforseo')) return 'DataForSEO';
  return source;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** em-dash for any null provider metric — never a fabricated value. */
function orNum(v: number | null, opts: { prefix?: string } = {}): string {
  if (v === null) return '—';
  return `${opts.prefix ?? ''}${formatInt(v)}`;
}

interface Panel {
  title: string;
  body: string;
}

/** One provider read's lifecycle — never react-query, so nothing auto-fires. */
type SectionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; panel: Panel }
  | { status: 'done'; data: T };

/** Turn an unknown fetch failure into honest, non-fabricating human guidance. */
function describeError(err: unknown): Panel {
  if (err instanceof ApiError) {
    if (err.status === 503 || err.code === 'provider_unavailable') {
      return {
        title: 'Domain intelligence isn’t connected yet',
        body: 'Live domain data needs the DataForSEO Labs connection, which isn’t configured for this environment yet. Nothing was fetched and nothing was charged — this is an honest “not configured” state, never placeholder data.',
      };
    }
    if (err.status === 402 || err.code === 'budget_exceeded') {
      return {
        title: 'Monthly provider budget reached',
        body: 'This lookup would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. It resumes when the budget resets, or after you raise the cap on Spend & Budget.',
      };
    }
    if (err.status === 429 || err.code === 'rate_limited') {
      return {
        title: 'Provider rate limit reached',
        body: 'This workspace has hit the provider rate limit, so nothing was fetched or charged. Try again as the window rolls over.',
      };
    }
    if (err.status === 502 || err.code === 'provider_error') {
      return {
        title: 'The provider request failed',
        body: `${err.message ? `${err.message} ` : ''}Nothing was stored or charged — try again in a moment.`,
      };
    }
    if (err.status === 404) {
      return {
        title: 'Project not found',
        body: 'This SEO project isn’t available to your workspace. Pick another project and try again.',
      };
    }
    return {
      title: 'The lookup could not be completed',
      body: err.message || 'The data service returned an unexpected response. Nothing was charged — try again.',
    };
  }
  return {
    title: 'The lookup could not be completed',
    body: err instanceof Error ? err.message : 'Unexpected error — nothing was charged. Try again.',
  };
}

/** Source + freshness + cache footer, shown on EVERY data card. */
function SourceFooter({
  source,
  fetchedAt,
  cacheHit,
}: {
  source: string;
  fetchedAt: string | null;
  cacheHit?: boolean;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-subtle pt-2 text-[11px] text-text-tertiary">
      <Badge tone="neutral">Source: {sourceLabel(source)}</Badge>
      {cacheHit !== undefined && (
        <Badge tone={cacheHit ? 'neutral' : 'success'}>
          {cacheHit ? 'Served from cache' : 'Live result'}
        </Badge>
      )}
      <span>{fetchedAt ? `Last updated ${fetchedAt}` : 'Update time not reported'}</span>
    </div>
  );
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div title={hint}>
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-0.5 font-mono text-lg font-semibold text-text-primary" data-tabular>
        {value}
      </div>
    </div>
  );
}

/** Humanise a distribution bucket key (e.g. pos_4_10 → "4–10"). */
function bucketLabel(key: string): string {
  const m = key.match(/(\d+)\D+(\d+)/);
  if (m) return `${m[1]}–${m[2]}`;
  const single = key.match(/(\d+)/);
  return single ? `#${single[1]}` : key;
}

const NUM_CELL = 'font-mono text-text-secondary';

/** GSC position / Provider position / Delta are sortable (client-side over the
 *  already-loaded benchmark rows — free, no reload). Query is non-sortable.
 *  Nulls sort last, so missing provider positions never invent an order. */
const BENCHMARK_COLUMNS: SortableColumn<BenchmarkComparison>[] = [
  {
    key: 'query',
    header: 'Query',
    render: (r) => r.query,
    truncate: true,
    maxWidthClass: 'max-w-[260px]',
    title: (r) => r.query,
    cellClassName: 'text-text-primary',
  },
  {
    key: 'gsc_position',
    header: 'GSC position',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (r) => r.gsc_position,
    render: (r) => (r.gsc_position === null ? '—' : r.gsc_position.toFixed(1)),
    cellClassName: NUM_CELL,
  },
  {
    key: 'provider_position',
    header: 'Provider position',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (r) => r.provider_position,
    render: (r) => (r.provider_position === null ? '—' : formatInt(r.provider_position)),
    cellClassName: NUM_CELL,
  },
  {
    key: 'delta',
    header: 'Delta',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (r) => r.delta,
    render: (r) =>
      r.delta === null ? '—' : r.delta > 0 ? `+${r.delta.toFixed(1)}` : r.delta.toFixed(1),
    cellClassName: NUM_CELL,
  },
];

/** The directional data-quality strip — GSC (ground truth) vs provider. */
function DataQualityStrip({
  state,
  canManage,
  onRun,
}: {
  state: SectionState<BenchmarkResult>;
  canManage: boolean;
  onRun: () => void;
}) {
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="ai">Data quality</Badge>
        <span className="text-sm font-medium text-text-primary">
          Directional benchmark — Search Console vs provider
        </span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-text-tertiary">GSC ground truth · one guarded sample</span>
          <Button
            variant="secondary"
            disabled={!canManage || state.status === 'loading'}
            onClick={onRun}
            title={canManage ? undefined : 'Owner/operator only — this uses a guarded paid sample'}
          >
            {state.status === 'loading' ? 'Checking…' : 'Run check'}
          </Button>
        </div>
      </div>
      <p className="mt-1 text-sm text-text-secondary">
        Compares your first-party Google Search Console positions against one guarded DataForSEO
        sample on overlapping queries. This is a <strong>directional</strong> confidence check
        (roadmap §9.2) — it flags divergence, it is never a hard pass/fail.
      </p>

      <div className="mt-3">
        {state.status === 'idle' && (
          <p className="text-sm text-text-tertiary">
            No check run yet — run it to see how closely the provider tracks Search Console.
          </p>
        )}
        {state.status === 'loading' && <LoadingState rows={2} />}
        {state.status === 'error' && (
          <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
            <div className="text-sm font-medium text-text-primary">{state.panel.title}</div>
            <p className="mt-1 text-sm text-text-secondary">{state.panel.body}</p>
          </div>
        )}
        {state.status === 'done' &&
          (state.data.benchmark === null || state.data.benchmark.gsc_vs_provider.length === 0 ? (
            <div className="rounded-md border border-border-subtle bg-canvas p-3">
              <p className="text-sm text-text-secondary">
                Not enough overlapping queries to compare yet — that’s an honest empty result, not an
                error. Sync more Search Console data, then re-run the check.
              </p>
              <SourceFooter source={state.data.source} fetchedAt={state.data.fetched_at} />
            </div>
          ) : (
            <div className="rounded-md border border-border-subtle bg-canvas p-3">
              <p className="text-sm text-text-primary">{state.data.benchmark.correlation_note}</p>
              <p className="mt-1 text-[11px] text-text-tertiary">
                Sample size: {formatInt(state.data.benchmark.sample_size)}{' '}
                {state.data.benchmark.sample_size === 1 ? 'query' : 'queries'}
              </p>
              <div className="mt-3">
                <SortableTable
                  rows={state.data.benchmark.gsc_vs_provider}
                  columns={BENCHMARK_COLUMNS}
                  getRowKey={(r) => r.query}
                  minWidthClass="min-w-[520px]"
                  caption="Directional GSC vs provider position comparison by query"
                />
              </div>
              <SourceFooter source={state.data.source} fetchedAt={state.data.fetched_at} />
            </div>
          ))}
      </div>
    </Card>
  );
}

export function SeoDomainOverviewPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const navigate = useNavigate();

  // Free list read — safe on mount. The paid overview/benchmark are never wired
  // to a query; they run on click only.
  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();

  const projectId = resolveActiveProjectId(active, projects);

  const [overview, setOverview] = useState<SectionState<DomainOverviewResult>>({ status: 'idle' });
  const [benchmark, setBenchmark] = useState<SectionState<BenchmarkResult>>({ status: 'idle' });

  const runOverview = async () => {
    if (projectId === null || overview.status === 'loading') return;
    setOverview({ status: 'loading' });
    try {
      const data = await fetchSeoDomainOverview(projectId);
      setOverview({ status: 'done', data });
    } catch (err) {
      setOverview({ status: 'error', panel: describeError(err) });
    }
  };

  const runBenchmark = async () => {
    if (projectId === null || benchmark.status === 'loading') return;
    setBenchmark({ status: 'loading' });
    try {
      const data = await fetchSeoBenchmark(projectId);
      setBenchmark({ status: 'done', data });
    } catch (err) {
      setBenchmark({ status: 'error', panel: describeError(err) });
    }
  };

  // Reset per-project results so one project's data never shows under another.
  const onProjectChange = (id: string) => {
    void select(Number(id));
    setOverview({ status: 'idle' });
    setBenchmark({ status: 'idle' });
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Domain Overview" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Domain Overview" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Domain Overview" />
        <EmptyState
          title="No project to explore yet"
          teach="Domain Overview runs against an SEO project. Add your first domain on the Projects page, then come back here to load its domain-level intelligence."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const inputClass =
    'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => onProjectChange(e.target.value)}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Domain Overview"
        subtitle="Domain-level organic intelligence from licensed DataForSEO Labs data — loaded on demand, always source-attributed, never a proprietary-index claim."
        actions={projectSelector}
      />

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          The domain overview and the data-quality check use paid provider data, so they’re limited
          to workspace owners and operators.
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">DataForSEO Labs</Badge>
          <span className="text-sm font-medium text-text-primary">Organic domain snapshot</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-tertiary">Paid · budget-guarded · 7-day cache</span>
            <Button
              variant="primary"
              disabled={!canManage || overview.status === 'loading'}
              onClick={() => void runOverview()}
              title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
            >
              {overview.status === 'loading'
                ? 'Loading…'
                : overview.status === 'done'
                  ? 'Refresh'
                  : 'Load domain overview'}
            </Button>
          </div>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Organic keyword footprint, estimated traffic and value, and rank distribution for the
          selected domain. Runs only when you press the button — nothing is fetched or charged on
          load.
        </p>

        <div className="mt-4">
          {overview.status === 'idle' && (
            <p className="text-sm text-text-tertiary">
              No data yet — load the overview to see this domain’s organic snapshot.
            </p>
          )}
          {overview.status === 'loading' && <LoadingState rows={3} />}
          {overview.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{overview.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{overview.panel.body}</p>
            </div>
          )}
          {overview.status === 'done' &&
            (overview.data.overview === null ? (
              <div className="rounded-md border border-border-subtle bg-canvas p-3">
                <p className="text-sm text-text-secondary">
                  The provider returned no overview for this domain — an honest empty result, not an
                  error.
                </p>
                <SourceFooter
                  source={overview.data.source}
                  fetchedAt={overview.data.fetched_at}
                  cacheHit={overview.data.cache_hit}
                />
              </div>
            ) : (
              <div className="rounded-md border border-border-subtle bg-canvas p-4">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <Metric label="Organic keywords" value={orNum(overview.data.overview.organic_keywords)} />
                  <Metric label="Organic traffic /mo" value={orNum(overview.data.overview.organic_traffic)} />
                  <Metric
                    label="Traffic value /mo"
                    value={orNum(overview.data.overview.organic_traffic_value, { prefix: '£' })}
                    hint="Estimated monthly value of the organic traffic, as the provider reports it"
                  />
                </div>

                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wide text-text-tertiary">
                    Rank distribution
                  </div>
                  {overview.data.overview.rank_distribution === null ? (
                    <p className="mt-1 text-sm text-text-secondary">
                      No rank distribution reported for this domain — shown honestly rather than
                      zero-filled.
                    </p>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(overview.data.overview.rank_distribution).map(([k, v]) => (
                        <div
                          key={k}
                          className="rounded-md border border-border-subtle bg-surface px-3 py-1.5"
                        >
                          <span className="text-[11px] text-text-tertiary">Pos {bucketLabel(k)}</span>
                          <span className="ml-2 font-mono text-sm font-semibold text-text-primary" data-tabular>
                            {v === null ? '—' : formatInt(v)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <SourceFooter
                  source={overview.data.source}
                  fetchedAt={overview.data.fetched_at}
                  cacheHit={overview.data.cache_hit}
                />
              </div>
            ))}
        </div>
      </Card>

      <DataQualityStrip state={benchmark} canManage={canManage} onRun={() => void runBenchmark()} />
    </div>
  );
}
